import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { API } from "../../utils/helpers";

const MAKAS_PRODUCTS = [
  { key: "ceyrek", label: "Çeyrek" },
  { key: "yarim", label: "Yarım" },
  { key: "teklik", label: "Teklik" },
  { key: "ata_lira", label: "Ata Lira" },
  { key: "gremse", label: "Gremse" },
  { key: "gram_1", label: "1 Gram" },
  { key: "gram_5", label: "5 Gram" },
  { key: "gram_10", label: "10 Gram" },
  { key: "gram_20", label: "20 Gram" },
  { key: "has_altin", label: "HAS Altın" },
];

const clampPct = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  if (isNaN(n)) return 50;
  return Math.max(0, Math.min(100, n));
};

const MakasModeCard = forwardRef(({ profile, marjMode, modeName }, ref) => {
  const [products, setProducts] = useState({});
  const [spreads, setSpreads] = useState({});
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/makas?profile=${profile}&marj_mode=${marjMode}`);
      setProducts(res.data.products || {});
    } catch (e) {
      console.error("Makas load error:", e);
    }
  }, [profile, marjMode]);

  const loadSpreads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/prices/live-spreads?profile=${profile}&marj_mode=${marjMode}`);
      setSpreads(res.data.spreads || {});
    } catch (e) {
      console.error("Live spreads error:", e);
    }
  }, [profile, marjMode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadSpreads();
    const t = setInterval(loadSpreads, 5000);
    return () => clearInterval(t);
  }, [loadSpreads]);

  const flash = (ok, text) => {
    if (ok) { setMsg(text); setTimeout(() => setMsg(""), 3000); }
    else { setErr(text); setTimeout(() => setErr(""), 3500); }
  };

  const toggle = (key) => {
    setProducts(prev => ({ ...prev, [key]: { ...(prev[key] || { spread: 0, alis_pct: 50 }), enabled: !(prev[key]?.enabled) } }));
  };
  const changeSpread = (key, value) => {
    setProducts(prev => ({ ...prev, [key]: { ...(prev[key] || { enabled: false, alis_pct: 50 }), spread: value } }));
  };
  const changeAlisPct = (key, value) => {
    setProducts(prev => ({ ...prev, [key]: { ...(prev[key] || { enabled: false, spread: 0 }), alis_pct: value } }));
  };

  const buildClean = useCallback(() => {
    const clean = {};
    MAKAS_PRODUCTS.forEach(p => {
      const c = products[p.key] || {};
      const s = String(c.spread ?? "").replace(",", ".");
      clean[p.key] = {
        enabled: !!c.enabled,
        spread: s === "" || s === "-" ? 0 : (parseFloat(s) || 0),
        alis_pct: clampPct(c.alis_pct ?? 50),
      };
    });
    return clean;
  }, [products]);

  const save = useCallback(async (silent = false) => {
    try {
      await axios.post(`${API}/admin/makas?profile=${profile}&marj_mode=${marjMode}`, { products: buildClean() });
      if (!silent) { flash(true, "Makas ayarları kaydedildi"); load(); }
      return true;
    } catch (e) {
      console.error("Makas save error:", e);
      if (!silent) flash(false, "Kaydetme hatası");
      throw e;
    }
  }, [profile, marjMode, buildClean, load]);

  // "Tümünü Kaydet" butonu bu save()'i cagirabilsin diye disa aciyoruz
  useImperativeHandle(ref, () => ({ save: () => save(true) }), [save]);

  const inputStyle = { width: 92, padding: "7px 9px", fontSize: 14, border: "1.5px solid #d0d0d0", borderRadius: 6, fontFamily: "monospace", textAlign: "right", background: "#e4e4e4" };
  const pctStyle = { width: 56, padding: "6px 7px", fontSize: 13, border: "1.5px solid #d0d0d0", borderRadius: 6, fontFamily: "monospace", textAlign: "right" };

  return (
    <div data-testid="makas-mode-card" style={{ background: "#e4e4e4", borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, color: "#09301E", fontSize: 15 }}>✂️ Makas Aralığı Modu — <span style={{ color: "#DA9F54" }}>{modeName || marjMode}</span></h3>
        <button onClick={() => save(false)} data-testid="makas-save-btn"
          style={{ padding: "8px 16px", background: "#DA9F54", color: "#000", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
          💾 Kaydet
        </button>
      </div>
      <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 12px" }}>
        En son adım. <b>Her mod için ayrı</b> kaydedilir (şu an: {modeName || marjMode}). Açık ürünlerde makas (satış − alış) hedefe eşitlenir.
        <b> Alış %</b> ile farkın ne kadarının alıştan, kalanının satıştan uygulanacağını her ürün için ayarlarsın (ör. %10 → alıştan %10, satıştan %90). %50 = eşit dağıtım. Kapalı ürünler eskisi gibi devam eder.
      </p>
      {msg && <p style={{ color: "#16a34a", fontSize: 13, margin: "0 0 8px", fontWeight: 700 }}>{msg}</p>}
      {err && <p style={{ color: "#ef4444", fontSize: 13, margin: "0 0 8px", fontWeight: 700 }}>{err}</p>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
        {MAKAS_PRODUCTS.map(p => {
          const c = products[p.key] || { enabled: false, spread: 0, alis_pct: 50 };
          const on = !!c.enabled;
          const live = spreads[p.key];
          const apNum = clampPct(c.alis_pct ?? 50);
          return (
            <div key={p.key} data-testid={`makas-row-${p.key}`}
              style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 10px", background: "#fff", borderRadius: 8, border: `1.5px solid ${on ? "#DA9F54" : "#e2e2e2"}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
                  <input type="checkbox" checked={on} onChange={() => toggle(p.key)} data-testid={`makas-toggle-${p.key}`}
                    style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#DA9F54" }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#09301E" }}>{p.label}</span>
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <input type="text" inputMode="decimal" value={c.spread ?? ""} disabled={!on}
                    onChange={e => changeSpread(p.key, e.target.value)} data-testid={`makas-spread-${p.key}`}
                    placeholder="Makas ₺"
                    style={{ ...inputStyle, background: on ? "#e4e4e4" : "#f5f5f5", color: on ? "#000" : "#999", cursor: on ? "text" : "not-allowed" }} />
                  <span style={{ fontSize: 12, color: "#64748b" }}>₺</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Alış %</span>
                  <input type="text" inputMode="decimal" value={c.alis_pct ?? ""} disabled={!on}
                    onChange={e => changeAlisPct(p.key, e.target.value)} data-testid={`makas-alispct-${p.key}`}
                    placeholder="50"
                    style={{ ...pctStyle, background: on ? "#fff" : "#f5f5f5", color: on ? "#000" : "#999", cursor: on ? "text" : "not-allowed" }} />
                  <span data-testid={`makas-satispct-${p.key}`} style={{ fontSize: 11, color: "#64748b" }}>
                    / Satış %<b style={{ color: "#09301E" }}>{(100 - apNum).toLocaleString("tr-TR")}</b>
                  </span>
                </div>
              </div>
              <div data-testid={`makas-live-${p.key}`} style={{ fontSize: 11, color: "#64748b", textAlign: "right" }}>
                Gerçek makas: <b style={{ color: "#09301E" }}>{live != null ? `${live.toLocaleString("tr-TR")} ₺` : "—"}</b>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

MakasModeCard.displayName = "MakasModeCard";

export default MakasModeCard;
